<script setup lang="ts" generic="T">
import type { StyleValue, VNode } from 'vue';
import { computed, watch, useTemplateRef } from 'vue';
import { useScroll } from '@vueuse/core';

import AppIconButton from '../appIconButton/AppIconButton.vue';

interface AppArrowScrollerProps {
  items: T[];
  itemKeySelector: (item: T, index: number) => number | string | symbol;
  displayDashedBorder: boolean;
  leftArrowAriaLabel: string;
  rightArrowAriaLabel: string;
  arrowVerticalPosition?: number;
  printMode?: boolean;
  scrollbarTheme?: 'scrollbar-violet' | 'scrollbar-neutral';
  showArrows?: boolean;
}
const props = withDefaults(defineProps<AppArrowScrollerProps>(), {
  scrollbarTheme: 'scrollbar-neutral',
  showArrows: true,
});

defineSlots<{
  item: (props: { item: T; index: number }) => VNode;
  footer?: () => VNode;
}>();

const slotRef = useTemplateRef('slotRef');

const scrollDistance = computed<number>(() => {
  if (!slotRef.value) {
    return 0;
  }
  const firstSlotElement = slotRef.value[0];

  if (!firstSlotElement) {
    return 0;
  }

  return firstSlotElement.offsetWidth;
});

const arrowAlignmentStyle = computed<StyleValue>(() => ({
  top: props.arrowVerticalPosition ? `${props.arrowVerticalPosition}px` : '50%',
}));

const contentRef = useTemplateRef('contentRef');
const { arrivedState, x: xScroll } = useScroll(contentRef, {
  behavior: 'smooth',
});

function scrollLeft(): void {
  xScroll.value -= scrollDistance.value;
}

function scrollRight(): void {
  xScroll.value += scrollDistance.value;
}

watch(
  () => props.items,
  () => {
    xScroll.value = 0;
  }
);
</script>

<template>
  <div
    class="app-arrow-scroller"
    :class="{ 'dashed-border': props.displayDashedBorder }"
  >
    <div :class="['app-arrow-scroller-content-wrapper', props.scrollbarTheme]">
      <AppIconButton
        v-if="!arrivedState.left && !props.printMode && props.showArrows"
        class="scroll-left-button"
        type="secondary"
        state="enabled"
        size="small"
        :ariaLabel="props.leftArrowAriaLabel"
        :icon="{ name: 'chevronLeft' }"
        :style="arrowAlignmentStyle"
        @click.stop="scrollLeft"
      />
      <AppIconButton
        v-if="!arrivedState.right && !props.printMode && props.showArrows"
        class="scroll-right-button"
        type="secondary"
        state="enabled"
        size="small"
        :ariaLabel="props.rightArrowAriaLabel"
        :icon="{ name: 'chevronRight' }"
        :style="arrowAlignmentStyle"
        @click.stop="scrollRight"
      />

      <div ref="contentRef" class="app-arrow-scroller-content">
        <template
          v-for="(item, index) in props.items"
          :key="props.itemKeySelector(item, index)"
        >
          <div ref="slotRef" class="app-arrow-scroller-item">
            <slot :item="item" :index="index" name="item" />
          </div>
        </template>
      </div>
    </div>

    <slot name="footer" />
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/_media' as *;
.app-arrow-scroller {
  &.dashed-border {
    padding: var(--spacing-12);
    border: 1px dashed var(--color-neutral-30);
    @include for-less-or-equal-than-1200-px {
      padding: 0;
      border: none;
    }
  }

  @include for-less-or-equal-than-1200-px {
    border-radius: none;
  }

  .app-arrow-scroller-content-wrapper {
    position: relative;

    .scroll-left-button,
    .scroll-right-button {
      position: absolute;
      display: none;
      z-index: 1;
      transform: translateY(-50%);

      @include for-less-or-equal-than-1200-px {
        display: none;
      }
    }

    .scroll-left-button {
      left: 20px;
    }
    .scroll-right-button {
      right: 20px;
    }

    @include on-hover {
      .scroll-left-button,
      .scroll-right-button {
        display: flex;
      }
    }

    &.scrollbar-neutral {
      ::-webkit-scrollbar-track {
        background: var(--color-off-white);
      }
    }

    &.scrollbar-violet {
      ::-webkit-scrollbar-track {
        background: var(--color-brand-violet);
      }

      ::-webkit-scrollbar-thumb {
        background-color: var(--color-white);
      }
    }

    .app-arrow-scroller-content {
      display: grid;
      grid-auto-rows: 1fr;
      grid-auto-flow: column;

      overflow-x: auto;
      overscroll-behavior-x: contain; // NOTE: this prevents going back/forward in the browser navigation when reaching the scroll limit
      overflow-y: hidden;

      -webkit-scroll-snap-type: x mandatory;
      -ms-scroll-snap-type: x mandatory;
      scroll-snap-type: x mandatory;

      .app-arrow-scroller-item {
        flex-shrink: 0;
        -webkit-scroll-snap-align: start;
        scroll-snap-align: start;
      }
    }
  }
}
</style>
